import { Link } from "react-router-dom";
import { Instagram, Twitter, Youtube } from "lucide-react";

/**
 * SRC-styled 5-column footer with social icons + policy links. Content is
 * driven by the template config so each store type gets its own brand name
 * / tagline / shop-by-category shortcuts / help links / company links.
 */
export default function Footer({ config, basePath = "" }) {
  const brand = config?.brand || {};
  const accent = brand.accentColor || "#E60000";
  const brandName = brand.name || "Store";
  const tagline = brand.tagline || config?.copy?.footer?.tagline ||
    "Small batches, made to last.";
  const categories = (config?.categories || []).slice(0, 5);
  const socials = config?.copy?.footer?.socials || [];
  const footerCopy = config?.copy?.footer || {};

  const helpLinks = config?.copy?.footer?.help || [
    { label: "Shipping", to: "#shipping" },
    { label: "Returns", to: "#returns" },
    { label: "Size Guide", to: "#size-guide" },
    { label: "Contact", to: `${basePath}/contact` },
    { label: "FAQ", to: "#faq" },
  ];
  const companyLinks = config?.copy?.footer?.company || [
    { label: "About", to: `${basePath}/about` },
    { label: "Journal", to: "#journal" },
    { label: "Sustainability", to: "#sustainability" },
    { label: "Press", to: "#press" },
  ];
  const legalLinks = config?.copy?.footer?.legal || [
    { label: "Privacy", to: "/privacy" },
    { label: "Terms", to: "/terms" },
    { label: "Cookies", to: "#cookies" },
  ];

  return (
    <footer data-testid="site-footer" className="bg-white border-t border-neutral-200">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-16 grid grid-cols-2 md:grid-cols-5 gap-10">
        <div className="col-span-2">
          <p className="font-display text-3xl">{brandName.toUpperCase()}<span style={{ color: accent }}>.</span></p>
          <p className="mt-4 text-sm text-neutral-600 max-w-sm leading-relaxed">{tagline}</p>
          <div className="flex items-center gap-3 mt-6">
            {(socials.length ? socials : [
              { icon: "instagram", href: "#" },
              { icon: "twitter", href: "#" },
              { icon: "youtube", href: "#" },
            ]).map((s, i) => {
              const Icon = s.icon === "twitter" ? Twitter : s.icon === "youtube" ? Youtube : Instagram;
              return (
                <a key={i} href={s.href || "#"} aria-label={s.icon}
                   className="w-9 h-9 grid place-items-center border border-neutral-300 hover:bg-black hover:text-white hover:border-black transition-colors">
                  <Icon className="w-4 h-4" strokeWidth={1.5} />
                </a>
              );
            })}
          </div>
        </div>

        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 mb-4" data-testid="footer-shop-heading">{footerCopy.shopHeading || "Shop"}</p>
          <ul className="space-y-2 text-sm">
            {categories.map((c) => (
              <li key={c.key || c.slug}>
                <Link to={`${basePath}/shop?category=${c.key || c.slug}`} className="link-underline pb-0.5">{c.title || c.label}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 mb-4" data-testid="footer-help-heading">{footerCopy.helpHeading || "Help"}</p>
          <ul className="space-y-2 text-sm">
            {helpLinks.map((l) => (
              <li key={l.label}><a href={l.to} className="link-underline pb-0.5">{l.label}</a></li>
            ))}
          </ul>
        </div>

        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 mb-4" data-testid="footer-company-heading">{footerCopy.companyHeading || "Company"}</p>
          <ul className="space-y-2 text-sm">
            {companyLinks.map((l) => (
              <li key={l.label}><a href={l.to} className="link-underline pb-0.5">{l.label}</a></li>
            ))}
          </ul>
        </div>
      </div>
      <div className="border-t border-neutral-200">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-neutral-500">
          <p data-testid="footer-copyright">{footerCopy.copyright || `© ${new Date().getFullYear()} ${brandName}. All rights reserved.`}</p>
          <div className="flex gap-4">
            {legalLinks.map((l) => (
              <a key={l.label} href={l.to} className="link-underline pb-0.5">{l.label}</a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
